<template>
  <v-card>
    <!-- Encabezado -->
    <v-card-title class="bg-orange text-white d-flex justify-space-between align-center">
      <div>
        <v-icon start>mdi-newspaper</v-icon>
        Gestión de Noticias
      </div>
      <v-btn color="white" variant="text" @click="openAddDialog">
        <v-icon start>mdi-plus</v-icon>
        Nueva Noticia
      </v-btn>
    </v-card-title>

    <!-- Tabla -->
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="noticias || []"
        :loading="loading"
        class="elevation-1"
      >
        <!-- Vigente -->
        <template #item.vigente="{ item }">
          <v-chip
            :color="item.vigente ? 'green' : 'red'"
            class="text-white"
            size="small"
            @click="toggleVigente(item)"
            style="cursor:pointer"
          >
            {{ item.vigente ? "Si" : "No" }}
          </v-chip>
        </template>

        <!-- Imagen -->
        <template #item.imagen="{ item }">
          <v-img
            v-if="item && (item.imagen_url || item.imagen)"
            :src="resolveNewsImageUrl(item)"
            max-width="70"
            max-height="50"
            cover
          />
          <span v-else>-</span>
        </template>

        <!-- Acciones -->
        <template #item.actions="{ item }">
          <v-btn icon="mdi-pencil" color="blue" variant="text" @click="openEditDialog(item)" />
          <v-btn icon="mdi-delete" color="red" variant="text" @click="confirmDelete(item)" />
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Back to Panel -->
    <div class="text-center mt-6 mb-6">
      <v-btn
        to="/admin"
        variant="text"
        color="grey-darken-1"
        prepend-icon="mdi-arrow-left"
      >
        Volver al Panel
      </v-btn>
    </div>

    <!-- Diálogo alta/edición -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card>
        <v-card-title class="bg-orange text-white">
          <span class="text-h5">{{ editMode ? 'Editar Noticia' : 'Nueva Noticia' }}</span>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="formData.titulo"
              label="Título"
              :rules="[rules.required]"
              variant="outlined"
              class="mb-4"
            />
            <v-textarea
              v-model="formData.descripcion"
              label="Descripción"
              :rules="[rules.required]"
              variant="outlined"
              rows="4"
              class="mb-4"
            />
            <v-file-input
              v-model="formData.imagen"
              label="Imagen (opcional): JPG, JPEG, PNG, WebP o GIF"
              accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
              :rules="[rules.imageFormat, rules.imageSize]"
              prepend-icon="mdi-image"
              variant="outlined"
              hint="Formatos permitidos: .jpg, .jpeg, .png, .webp y .gif. Tamaño máximo: 5 MB."
              persistent-hint
              class="mb-4"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeDialog">Cancelar</v-btn>
          <v-btn color="orange" variant="flat" @click="saveNoticia" :disabled="!valid">
            {{ editMode ? 'Actualizar' : 'Guardar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmación eliminación -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>¿Eliminar noticia?</v-card-title>
        <v-card-text>Esta acción no se puede deshacer.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="red" variant="flat" @click="deleteNoticia">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, onMounted, computed } from "vue"
import { useStore } from "vuex"
import { resolveNewsImageUrl } from "@/utils/resolveUploadUrl"

const store = useStore()

const loading = ref(false)
const dialog = ref(false)
const deleteDialog = ref(false)
const editMode = ref(false)
const valid = ref(false)
const selectedNoticia = ref(null)

const formData = ref({
  id_noticia: null,
  titulo: "",
  descripcion: "",
  imagen: null,
})

const headers = [
  { title: "ID", key: "id_noticia" },
  { title: "Título", key: "titulo" },
  { title: "Fecha", key: "fecha_publicacion" },
  { title: "Imagen", key: "imagen" },
  { title: "Vigente", key: "vigente" },
  { title: "Acciones", key: "actions", sortable: false },
]

const rules = {
  required: (v) => !!v || "Campo obligatorio",
  imageFormat: (value) => {
    if (!value) return true

    const file = Array.isArray(value) ? value[0] : value
    if (!file) return true

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"]
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp", ".gif"]
    const fileName = file.name?.toLowerCase() || ""
    const hasAllowedExtension = allowedExtensions.some(extension => fileName.endsWith(extension))

    return (allowedTypes.includes(file.type) && hasAllowedExtension)
      || "Formato no permitido. Usa JPG, JPEG, PNG, WebP o GIF."
  },
  imageSize: (value) => {
    if (!value) return true

    const file = Array.isArray(value) ? value[0] : value
    return !file || file.size <= 5 * 1024 * 1024
      || "La imagen debe pesar como máximo 5 MB."
  },
}

// 🔸 Cargar todas las noticias
const fetchNoticias = async () => {
  loading.value = true
  const result = await store.dispatch("noticias/fetchNoticias")
  if (!result?.success) {
    console.error("Error cargando noticias:", result?.message)
  }
  loading.value = false
}

const noticias = computed(() => {
  const items = store.getters["noticias/getAllNoticias"]
  return Array.isArray(items) ? items.filter(Boolean) : []
})

// 🔸 Abrir diálogo nuevo
const openAddDialog = () => {
  editMode.value = false
  formData.value = { id_noticia: null, titulo: "", descripcion: "", imagen: null }
  dialog.value = true
}

// 🔸 Abrir edición
const openEditDialog = (item) => {
  editMode.value = true
  formData.value = { ...item, imagen: null } // no se carga imagen existente
  dialog.value = true
}

// 🔸 Cerrar diálogo
const closeDialog = () => {
  dialog.value = false
  formData.value = { id_noticia: null, titulo: "", descripcion: "", imagen: null }
}

// 🔸 Guardar noticia
const saveNoticia = async () => {
  if (!valid.value) return

  const body = new FormData()
  body.append("titulo", formData.value.titulo)
  body.append("descripcion", formData.value.descripcion)
  if (formData.value.imagen && formData.value.imagen instanceof File) {
    body.append("imagen", formData.value.imagen)
  }
  if (editMode.value) {
    body.append("id_noticia", formData.value.id_noticia)
  }

  const action = editMode.value ? "updateNoticia" : "addNoticia"
  const result = await store.dispatch(`noticias/${action}`, body)

  if (result.success) {
    dialog.value = false
    await fetchNoticias()
  } else {
    alert(result.message || "Error al guardar la noticia")
  }
}

// 🔸 Confirmar eliminación
const confirmDelete = (item) => {
  selectedNoticia.value = item
  deleteDialog.value = true
}

// 🔸 Eliminar noticia
const deleteNoticia = async () => {
  if (!selectedNoticia.value?.id_noticia) return
  const result = await store.dispatch("noticias/deleteNoticia", selectedNoticia.value.id_noticia)
  if (result.success) {
    await fetchNoticias()
  } else {
    alert(result.message || "Error al eliminar la noticia")
  }
  deleteDialog.value = false
}

const toggleVigente = async (item) => {
  const nuevoEstado = item.vigente ? 0 : 1
  await store.dispatch("noticias/toggleVigente", { id: item.id_noticia, vigente: nuevoEstado })
}

onMounted(() => fetchNoticias())
</script>
